import { lazy, memo, Suspense, useEffect, useState } from "react";

import { ErrorScreenDesktop } from "../ErrorScreenDesktop";
import { MicroFrontendSkeleton } from "./MicroFrontendSkeleton";

declare var __webpack_init_sharing__: Function;
declare var __webpack_share_scopes__: any;

/**
 * @module
 * MicroFrontend
 *
 * @category
 * UI Components (navigation)
 *
 * @see https://webpack.js.org/concepts/module-federation/
 * @see https://webpack.js.org/concepts/module-federation/#dynamic-remote-containers
 * @see https://module-federation.github.io/videos/advanced-shared-api-module-federation-for-power-users
 *
 * @description
 * This component will be used to dynamically load micro-frontend containers/components from a remote entry point.
 * The MicroFrontend component should be activated on a particular route as shown in the example, noting that all
 * properties are required. The entry point and components exposed are defined in each micro-frontend's webpack
 * configuration in the plugin section via the ModuleFederationPlugin.
 *
 * Note that loading errors are logged via our metrics module in the event that a micro-frontend fails to load.
 *
 * @example
 *  // Micro-Frontend Webpack configuration to expose say "VideoCommerce" (snipped from plugins section in webpack.config.js).
 *  plugins: [
 *      new ModuleFederationPlugin({
 *          name: 'VideoCommerce',
 *          filename: 'remoteEntry.js',
 *          exposes: {
 *              './VideoCommerce': './src/App.tsx',
 *          },
 *          // Define shared libraries as singletons so that they are only loaded once and don't fallback to another version.
 *          shared: {
 *              react: { singleton: true },
 *              'react-dom': { singleton: true },
 *          },
 *      }),
 *
 *      // other plugins
 *  ]
 *
 * @prop {string} module
 * The exposed module/component to render, e.g. "./VideoCommerce", which is
 * defined in the micro-frontend’s webpack configuration via the ModuleFederationPlugin.
 *
 * @prop {string} scope
 * This is the name that the micro-frontend defines via the ModuleFederationPlugin
 * and must be unique to avoid name collisions. e.g. VideoCommerce.
 *
 * @prop {remoteEntryPointUrl} string
 * The remote entry point, e.g. “https://localhost:3030/remoteEntry.js”.
 * This will vary depending on the environment.
 */
export const MicroFrontend = memo((props: any) => {
  const { module, scope, remoteEntryPointUrl } = props;
  const [isFailed, setIsFailed] = useState(false);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    setIsReady(false);
    setIsFailed(false);

    const element = document.createElement("script");

    element.async = true;
    element.src = remoteEntryPointUrl;
    element.type = "text/javascript";
    element.addEventListener("load", handleLoad);
    element.addEventListener("error", handleError);

    document.head.appendChild(element);

    return () => {
      element.removeEventListener("load", handleLoad);
      element.removeEventListener("error", handleError);
      document.head.removeChild(element);
    };
  }, [remoteEntryPointUrl]);

  function handleError() {
    //TODO: Track error in RUM if MFE load fails and alarms created
    setIsReady(false);
    setIsFailed(true);
  }

  function handleLoad() {
    //TODO: To calculate the time required to load a MFE and pass it to RUM
    // To create alarms if MFE takes more than a specified benchmark time to load.
    setIsReady(true);
  }

  function loadComponent() {
      type KeyValue = {
        [x: string]: any;
      };

      return async () => {
        // Initializes the share scope. This fills it with known provided modules from this build and all remotes
        await __webpack_init_sharing__("default");

        // Initialize the container so that it may provide shared modules
        const container = (window as KeyValue)[scope];
        // eslint-disable-next-line camelcase
        await container.init(__webpack_share_scopes__.default);
        const factory = await (window as KeyValue)[scope].get(module);
        const Module = factory();

        return Module;
      };
  }

  if (isFailed) {
    return <ErrorScreenDesktop />;
  }

  if (!isReady) {
    return <MicroFrontendSkeleton />;
  }

  const Component = lazy(loadComponent());

  return (
    <Suspense fallback={<MicroFrontendSkeleton />}>
      <Component {...props} />
    </Suspense>
  );
});
